import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { PortalCatchAll } from "../components/feature/PortalHostGuard";

const ResetPasswordPage = lazy(() => import("../pages/reset-password/page"));
const PartnerPortalPage = lazy(() => import("../pages/partner-portal/page"));
const CustomerLoginPage = lazy(() => import("../pages/customer-login/page"));
const MyOrdersPage = lazy(() => import("../pages/my-orders/page"));
const AccountCheckoutPage = lazy(() => import("../pages/account-checkout/page"));
// PawTenant runs ONE sign-in for staff and providers (the "Staff & Provider
// Portal" at /admin-login). /provider-login is a forwarder to it that turns
// ?returnUrl= into ?next=, so the provider host mounts both.
const ProviderLoginPage = lazy(() => import("../pages/provider-login/page"));
const StaffProviderLoginPage = lazy(() => import("../pages/admin-login/page"));
const ProviderPortalPage = lazy(() => import("../pages/provider-portal/page"));

function PageLoader({ label }: { label: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f0faf7]">
      <div className="flex flex-col items-center gap-3">
        <i className="ri-loader-4-line animate-spin text-3xl text-[#1a5c4f]"></i>
        <span className="text-sm text-[#1a5c4f]/60 font-medium">{label}</span>
      </div>
    </div>
  );
}

function P({ C, label }: { C: React.ComponentType; label: string }) {
  return (
    <Suspense fallback={<PageLoader label={label} />}>
      <C />
    </Suspense>
  );
}

// PAWTENANT-BRANDED-PORTAL-HOSTS-LIVE-001: every shell's catch-all re-hosts a
// route another portal owns (src/lib/portalHosts.ts) and otherwise falls back
// to the shell's own landing page. /reset-password is mounted on every shell
// because Supabase returns recovery credentials in the URL hash.

/** partner.pawtenant.com — invite-only partner organisation portal. */
export function PartnerSubdomainRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/partner-portal" replace />} />
      <Route path="/partner-portal" element={<P C={PartnerPortalPage} label="Loading partner portal..." />} />
      <Route path="/reset-password" element={<P C={ResetPasswordPage} label="Loading secure access..." />} />
      <Route path="*" element={<PortalCatchAll fallback="/partner-portal" />} />
    </Routes>
  );
}

/** provider.pawtenant.com — licensed provider portal, provider routes only. */
export function ProviderSubdomainRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/provider-login" replace />} />
      <Route path="/provider-login" element={<P C={ProviderLoginPage} label="Loading provider portal..." />} />
      <Route path="/admin-login" element={<P C={StaffProviderLoginPage} label="Loading provider portal..." />} />
      <Route path="/provider-portal" element={<P C={ProviderPortalPage} label="Loading provider portal..." />} />
      <Route path="/reset-password" element={<P C={ResetPasswordPage} label="Loading secure access..." />} />
      <Route path="*" element={<PortalCatchAll fallback="/provider-login" />} />
    </Routes>
  );
}

/**
 * customer.pawtenant.com — customer sign-in, orders and documents. Payment
 * links (/checkout/<slug>) are built on pawtenant.com and stay there; the
 * catch-all sends any that land here back to the public site.
 */
export function CustomerSubdomainRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/customer-login" replace />} />
      <Route path="/customer-login" element={<P C={CustomerLoginPage} label="Loading customer portal..." />} />
      <Route path="/my-orders" element={<P C={MyOrdersPage} label="Loading your orders..." />} />
      <Route path="/account/checkout" element={<P C={AccountCheckoutPage} label="Loading checkout..." />} />
      <Route path="/reset-password" element={<P C={ResetPasswordPage} label="Loading secure access..." />} />
      <Route path="*" element={<PortalCatchAll fallback="/customer-login" />} />
    </Routes>
  );
}
